import { CxReferenceError } from '@proc7ts/context-values';
import { noop, lazyValue } from '@proc7ts/primitives';
import { Supply } from '@proc7ts/supply';
import { onNever, EventEmitter, trackValue, mapAfter_, deduplicateAfter_ } from '@proc7ts/fun-events';
import { itsElements, flatMapIt, reverseArray } from '@proc7ts/push-iterator';

/**
 * Creates aliasing context entry asset.
 *
 * @typeParam TAsset - Context value asset type.
 * @param entry - Target context entry.
 * @param alias - Context entry which value is used as an asset of the `target` entry.
 * @param supply - Asset supply. Removes the created asset once cut off.
 *
 * @returns New context entry asset.
 */
function cxAliasAsset(entry, alias, supply) {
    return {
        entry,
        placeAsset(target, collector) {
            try {
                target.get(alias, {
                    set(asset) {
                        collector(asset);
                    },
                });
            }
            catch (reason) {
                if (reason instanceof CxReferenceError) {
                    throw new CxReferenceError(target.entry, undefined, reason);
                }
                throw reason;
            }
        },
        supply,
    };
}

/**
 * Creates context entry asset that builds value asset with the given builder function.
 *
 * Evaluates value asset at most once per context.
 *
 * @typeParam TValue - Context value type.
 * @typeParam TAsset - Context value asset type.
 * @typeParam TContext - Supported context type.
 * @param entry - Target context entry.
 * @param build - Asset builder function accepting entry definition target as its only parameter.
 * @param supply - Asset supply. Removes the created asset once cut off.
 *
 * @returns New context entry asset.
 */
function cxBuildAsset(entry, build, supply) {
    return {
        entry,
        buildAsset(target) {
            const asset = build(target);
            return asset != null && (collector => collector(asset));
        },
        supply,
    };
}

/**
 * Creates constant context entry asset.
 *
 * @typeParam TAsset - Context value asset type.
 * @param entry - Target context entry.
 * @param value - Constant value asset, or `null`/`undefined` to not provide any assets.
 * @param supply - Asset supply. Removes the created asset once cut off.
 *
 * @returns New context entry asset.
 */
function cxConstAsset(entry, value, supply) {
    return {
        entry,
        placeAsset: value != null
            ? (_target, collector) => collector(value)
            : CxAsset$provideNone,
        supply,
    };
}
function CxAsset$provideNone(_target, _collector) {
    // No assets.
}

/**
 * Creates context entry asset that tracks for value asset updates.
 *
 * Starts tracking at most once per context.
 *
 * @typeParam TValue - Context value type.
 * @typeParam TAsset - Context value asset type.
 * @typeParam TContext - Supported context type.
 * @param entry - Target context entry.
 * @param track - Starts assets tracking. Accepts context entry definition target, assets receiver function, and assets
 * supply as parameters. Passes updated assets to receiver until supply cut off.
 * @param supply - Asset supply. Removes the created asset once cut off.
 *
 * @returns New context entry asset.
 */
function cxTrackAsset(entry, track, supply = new Supply()) {
    return {
        entry,
        buildAsset(target, update) {
            let assets = [];
            let sendUpdate = noop;
            track(target, (...updates) => {
                assets = updates;
                sendUpdate();
            }, new Supply().needs(supply).needs(target));
            sendUpdate = update; // Start actually sending updates only after receiver registration.
            return collector => {
                for (const asset of assets) {
                    if (collector(asset) === false) {
                        break;
                    }
                }
            };
        },
        supply,
    };
}

class CxBuilder$BoundPeer {
    constructor(_cb, _cache) {
        this._cb = _cb;
        this._cache = _cache;
    }
    get supply() {
        return this._cb.supply;
    }
    get rankCount() {
        return this._cb.rankCount;
    }
    eachAsset(target, _cache, callback) {
        const record = this._cb._record(target.entry);
        record.eachAsset(record.target, this._cache, callback);
    }
    eachRecentAsset(target, _cache, callback) {
        const record = this._cb._record(target.entry);
        record.eachRecentAsset(record.target, this._cache, callback);
    }
    trackAssets(target, _cache, receiver, tracking) {
        const record = this._cb._record(target.entry);
        return record.trackAssets(record.target, this._cache, receiver, tracking);
    }
}

class CxBuilder$Cache {
    constructor() {
        this._ = new Map();
    }
    get(key) {
        return this._.get(key);
    }
    put(key, value, supply) {
        this._.set(key, value);
        supply.whenOff(() => this._.delete(key));
    }
}
const CxBuilder$NoCache = {
    get: noop,
    put: noop,
};

function CxAsset$placerOf(asset, supply) {
    if (asset.placeAsset) {
        return {
            onUpdate: onNever,
            place(target, _cache, collector) {
                asset.placeAsset(target, collector);
            },
        };
    }
    if (asset.buildAsset) {
        const key = { asset };
        const updates = new EventEmitter();
        return {
            onUpdate: updates.on,
            place(target, cache, collector) {
                let provider = cache.get(key);
                if (provider === undefined) {
                    provider = asset.buildAsset(target, () => updates.send()) || CxAsset$noneProvider;
                    cache.put(key, provider, supply);
                }
                provider(collector);
            },
        };
    }
}
function CxAsset$noneProvider(_collector) {
    // No assets.
}

class CxAsset$Provided {
    constructor(_target, _cache, _placer, supply) {
        this._target = _target;
        this._cache = _cache;
        this._placer = _placer;
        this.supply = supply;
        this._getRecent = this._evalRecent();
        this._getRecent = this._evalRecent();
    }
    get rank() {
        return 0;
    }
    get recentAsset() {
        return this._getRecent();
    }
    eachAsset(callback) {
        this._placer.place(this._target, this._cache, callback);
    }
    eachRecentAsset(callback) {
        const assets = [];
        this.eachAsset(asset => {
            assets.push(asset);
        });
        for (let i = assets.length - 1; i >= 0; --i) {
            if (callback(assets[i]) === false) {
                break;
            }
        }
    }
    onUpdate(receiver, { supply = new Supply() } = {}) {
        return this._placer.onUpdate({
            receive: _ctx => {
                this._getRecent = this._evalRecent(); // Re-evaluate the most recent value next time it is requested.
                receiver();
            },
            supply: supply.needs(this.supply),
        });
    }
    _evalRecent() {
        return lazyValue(() => {
            let recent;
            this.eachRecentAsset(asset => {
                recent = {
                    asset,
                    rank: this.rank,
                    supply: this.supply,
                };
                return false;
            });
            return recent;
        });
    }
}
class CxAsset$Derived {
    constructor($, rank) {
        this.$ = $;
        this.rank = rank;
    }
    get supply() {
        return this.$.supply;
    }
    get recentAsset() {
        return this.$.recentAsset;
    }
    eachAsset(callback) {
        return this.$.eachAsset(callback);
    }
    eachRecentAsset(callback) {
        return this.$.eachRecentAsset(callback);
    }
    onUpdate(receiver, tracking) {
        return this.$.onUpdate(receiver, tracking);
    }
}

function CxEntry$assetsByRank(target, trackingSupply) {
    const assetsByRank = trackValue([]);
    assetsByRank.supply.needs(trackingSupply).needs(target);
    const removeAsset = (supply, rank) => {
        const ranks = [...assetsByRank.it];
        const rankAssets = ranks[rank];
        rankAssets.delete(supply);
        assetsByRank.it = ranks;
    };
    const updateAsset = () => assetsByRank.it = [...assetsByRank.it];
    const addAsset = (asset) => {
        const { supply, rank } = asset;
        const ranks = [...assetsByRank.it];
        for (let i = ranks.length; i <= rank; ++i) {
            ranks[i] || (ranks[i] = new Map());
        }
        const rankAssets = ranks[rank];
        rankAssets.set(supply, asset);
        assetsByRank.it = ranks;
        supply.whenOff(() => removeAsset(supply, rank));
        asset.onUpdate(updateAsset);
    };
    target.trackAssets(addAsset, assetsByRank);
    return assetsByRank;
}
function CxEntry$recentAsset(assetsByRank) {
    for (const rankAssets of assetsByRank) {
        const list = itsElements(rankAssets.values());
        for (let i = list.length - 1; i >= 0; --i) {
            const recent = list[i].recentAsset;
            if (recent) {
                return recent;
            }
        }
    }
    return;
}

class CxEntry$Target {
    constructor(_record, supply) {
        this._record = _record;
        this.supply = supply;
    }
    get entry() {
        return this._record.entry;
    }
    get context() {
        return this._record.builder.context;
    }
    get recentAsset() {
        let mostRecent;
        this.eachRecentAsset(asset => {
            mostRecent = asset;
            return false;
        });
        return mostRecent;
    }
    get(entry, request) {
        return this.context.get(entry, request);
    }
    provide(asset) {
        return this._record.builder.provide(asset).needs(this);
    }
    eachAsset(callback) {
        this._record.builder.eachAsset(this, this._record.cache, callback);
    }
    eachRecentAsset(callback) {
        this._record.builder.eachRecentAsset(this, this._record.cache, callback);
    }
    trackAssets(receiver, tracking) {
        return this._record.builder.trackAssets(this, this._record.cache, receiver, tracking);
    }
    trackRecentAsset(receiver, { supply = new Supply() } = {}) {
        return CxEntry$assetsByRank(this, supply).read.do(mapAfter_(CxEntry$recentAsset), deduplicateAfter_())(receiver);
    }
    trackAssetList(receiver, { supply = new Supply() } = {}) {
        return CxEntry$assetsByRank(this, supply).read.do(mapAfter_(assetByRank => itsElements(flatMapIt(reverseArray(assetByRank), rankAssets => rankAssets.values()))))(receiver);
    }
    lazy(evaluator) {
        return lazyValue(() => evaluator(this));
    }
}

class CxEntry$Record {
    constructor(builder, cache, entry) {
        this.builder = builder;
        this.cache = cache;
        this.entry = entry;
        this.placers = new Map();
        this.senders = new Map();
        this.supply = new Supply().needs(builder.supply);
        this.target = new CxEntry$Target(this, new Supply().needs(builder.supply));
        this.define = lazyValue(() => entry.perContext(this.target));
    }
    provide(asset) {
        var _a;
        const { supply = new Supply() } = asset;
        if (supply.needs(this).isOff) {
            return supply;
        }
        const placer = CxAsset$placerOf(asset, supply);
        if (placer) {
            const placerSupply = new Supply().as(supply);
            this.placers.set(placerSupply, placer);
            placerSupply.whenOff(() => this.placers.delete(placerSupply));
            for (const [trackingSupply, sender] of this.senders) {
                this.sendAssets(sender, placer, new Supply().needs(supply).needs(trackingSupply));
            }
        }
        (_a = asset.setupAsset) === null || _a === void 0 ? void 0 : _a.call(asset, new CxEntry$Target(this, supply));
        return supply;
    }
    get(request = {}) {
        var _a, _b;
        const { by = 0 /* Fallback */, or, set = CxEntry$dontSet } = request;
        const definition = this.define();
        let resultBy;
        let result;
        if (by >= 0 /* unless default value requested */) {
            // Request explicitly provided value.
            (_a = definition.assign) === null || _a === void 0 ? void 0 : _a.call(definition, (value, by = 1 /* Assets */) => {
                resultBy = by;
                result = value;
            }, request);
            if (resultBy != null /* value received */) {
                if (resultBy >= 0 /* either explicitly provided, or fallback value received */) {
                    // Report and return it.
                    set(result, resultBy);
                    return result;
                }
                // Default value received...
                if (by /* ...but explicitly provided value requested */
                    && or === undefined /* ...and no fallback specified */) {
                    // This is not acceptable.
                    throw new CxReferenceError(this.entry, `No value provided for ${this.entry}`);
                }
                // ...go on with default value.
            }
            if (or !== undefined /* fallback specified */) {
                // Report and return fallback value.
                set(or, 0 /* Fallback */);
                return or;
            }
        }
        if (resultBy == null /* unless (default) value received already */) {
            if (definition.assignDefault) {
                // Request default value.
                definition.assignDefault((value, by = -1 /* Defaults */) => {
                    resultBy = by;
                    result = value;
                }, request);
            }
            else if (by < 0 /* default value requested and there is no `assignDefault()` method */) {
                // Request the value with `assign()` method, is it may provide default value instead.
                (_b = definition.assign) === null || _b === void 0 ? void 0 : _b.call(definition, (value, by = 1 /* Assets */) => {
                    resultBy = by;
                    result = value;
                }, request);
            }
        }
        if (resultBy != null /* value received (from either `assign()` or `assignDefault()` call) */) {
            if (resultBy <= 0 /* either default, or fallback value received */) {
                // Report and return it.
                set(result, resultBy);
                return result;
            }
            if (or === undefined /* no fallback specified */) {
                // Explicitly provided value received...
                if (by < 0 /* ...but default value requested */) {
                    // This is not acceptable.
                    throw new CxReferenceError(this.entry, `The ${this.entry} has no default value`);
                }
                // ...report and return explicitly provided value.
                set(result, resultBy);
                return result;
            }
        }
        // No acceptable value received.
        if (by < 0 /* default value requested */ && or !== undefined /* ...and fallback specified */) {
            // Report and return fallback value.
            set(or, 0 /* Fallback */);
            return or;
        }
        // Nothing to return.
        throw new CxReferenceError(this.entry);
    }
    eachAsset(target, cache, callback) {
        if (target.supply.isOff) {
            return;
        }
        let goOn = true;
        const collector = asset => goOn = !target.supply.isOff
            && callback(asset) !== false
            && !target.supply.isOff;
        for (const peer of this.builder._peers) {
            peer.eachAsset(target, cache, collector);
            if (!goOn) {
                return;
            }
        }
        for (const placer of this.placers.values()) {
            placer.place(target, cache, collector);
            if (!goOn) {
                break;
            }
        }
    }
    eachRecentAsset(target, cache, callback) {
        if (target.supply.isOff) {
            return;
        }
        let goOn = true;
        const collector = asset => goOn = !target.supply.isOff
            && callback(asset) !== false
            && !target.supply.isOff;
        // Iterate in most-recent-first order.
        for (const placer of [...this.placers.values()].reverse()) {
            placer.place(target, cache, collector);
            if (!goOn) {
                return;
            }
        }
        // Do the same for peers in most-recent-first order.
        const peers = this.builder._peers;
        for (let i = peers.length - 1; i >= 0; --i) {
            peers[i].eachRecentAsset(target, cache, collector);
            if (!goOn) {
                return;
            }
        }
    }
    trackAssets(target, cache, receiver, { supply = new Supply() } = {}) {
        const rcv = {
            supply,
            receive(_ctx, asset) {
                receiver(asset);
            },
        };
        const emitter = new EventEmitter();
        emitter.supply.needs(target);
        emitter.on(rcv);
        const sender = [target, cache, emitter];
        this.senders.set(supply, sender);
        supply.whenOff(() => this.senders.delete(supply));
        let rankOffset = 1;
        const peers = this.builder._peers;
        for (let i = peers.length - 1; i >= 0; --i) {
            const peer = peers[i];
            const firstRank = rankOffset;
            rankOffset += peer.rankCount;
            peer.trackAssets(target, cache, provided => receiver(new CxAsset$Derived(provided, firstRank + provided.rank)), supply);
        }
        for (const [placerSupply, placer] of this.placers) {
            this.sendAssets(sender, placer, new Supply().needs(placerSupply).needs(supply));
        }
        return supply;
    }
    sendAssets([target, cache, emitter], placer, supply) {
        emitter.send(new CxAsset$Provided(target, cache, placer, supply));
    }
}
function CxEntry$dontSet(_value, _by) {
    // Do not set the value.
}

/**
 * Context peer builder.
 *
 * Allows to specify value assets preceding those from {@link CxBuilder.provide context builder}.
 *
 * In contrast to {@link CxBuilder context builder} this one does not build any context. It is an error accessing it.
 *
 * @typeParam TContext - Context type the assets provided for.
 */
class CxPeerBuilder {
    /**
     * Constructs context peer builder.
     *
     * @param peers - Context peers to apply assets from. These assets applied before the ones provided
     * @param peers - Context peers to apply assets from. These assets applied before the ones provided {@link provide
     * explicitly}. Peers listed later have lesser {@link CxAsset.Provided.rank rank values} than the ones listed earlier.
     */
    constructor(...peers) {
        /**
         * @internal
         */
        this._records = new Map();
        /**
         * @internal
         */
        this._supply = new Supply();
        /**
         * @internal
         */
        this._rankCount = 0;
        this._peers = peers;
    }
    get context() {
        throw new TypeError('Peer context is not available');
    }
    get rankCount() {
        return this._rankCount || (this._rankCount = this._peers.reduce((rankCount, peer) => rankCount + peer.rankCount, 1));
    }
    get supply() {
        return this._supply;
    }
    get cache() {
        return CxBuilder$NoCache;
    }
    provide(asset) {
        return this._record(asset.entry).provide(asset);
    }
    eachAsset(target, cache, callback) {
        this._record(target.entry).eachAsset(target, cache, callback);
    }
    eachRecentAsset(target, cache, callback) {
        this._record(target.entry).eachRecentAsset(target, cache, callback);
    }
    trackAssets(target, cache, receiver, tracking) {
        return this._record(target.entry).trackAssets(target, cache, receiver, tracking);
    }
    /**
     * @internal
     */
    _record(entry) {
        let record = this._records.get(entry);
        if (!record) {
            this._records.set(entry, record = new CxEntry$Record(this, this.cache, entry));
        }
        return record;
    }
}

/**
 * Context builder.
 *
 * Provides value assets for the context.
 *
 * @typeParam TContext - A type of context to build.
 */
class CxBuilder extends CxPeerBuilder {
    /**
     * Constructs context builder.
     *
     * @param createContext - Context creator function. Accepts context value accessor and the builder itself as
     * parameters, and returns created context.
     * @param peers - Context peers to apply assets from. These assets applied before the ones provided {@link provide
     * explicitly}. Peers listed later have lesser {@link CxAsset.Provided.rank rank values} than the ones listed earlier.
     */
    constructor(createContext, ...peers) {
        super(...peers);
        /**
         * @internal
         */
        this._cache = new CxBuilder$Cache();
        /**
         * @internal
         */
        this._bound = lazyValue(() => new CxBuilder$BoundPeer(this, this.cache));
        this._cx = lazyValue(() => createContext((entry, request) => this.get(entry, request), this));
    }
    /**
     * Context to build.
     */
    get context() {
        return this._cx();
    }
    get cache() {
        return this._cache;
    }
    /**
     * A peer providing assets bound to {@link context}.
     *
     * Unlike the builder itself, this peer may provide assets for any context, as they constructed in compatible one.
     */
    get boundPeer() {
        return this._bound();
    }
    get(entry, request) {
        return this._record(entry).get(request);
    }
}

export { CxBuilder, CxPeerBuilder, cxAliasAsset, cxBuildAsset, cxConstAsset, cxTrackAsset };
//# sourceMappingURL=context-builder.js.map
